import { useGetAllNews } from "@/api/cms";
import { NewsItemDrawer } from "@/components/cms/NewsItemDrawer";
import { Button } from "@/components/ui/button";
import type { NewsItem } from "@/lib/cms/cms-types";
import { normalizeNewsIds } from "@/lib/cms-puck/blocks/news-display";
import { useCmsNew, visibleT } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase } from "@/lib/cms/cms-new-phrases";
import type { SupportedLocale } from "@/i18n";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

function newsTitle(item: NewsItem, lang: string): string {
  const t = item.title as Record<string, string> | undefined;
  return (
    t?.[lang as SupportedLocale] ??
    t?.fr ??
    visibleT("cms.new.pickers.news.untitled", "(Sans titre)")
  );
}

/**
 * Liste à cocher d'actualités (mode manuel du bloc News) + création via le
 * même drawer que `/cms/news` / `/cms/advanced/news`.
 */
export function NewsIdsField({
  value,
  onChange,
}: {
  value: string[] | undefined;
  onChange: (value: string[]) => void;
}) {
  const cmsNew = useCmsNew();
  const newsIds = normalizeNewsIds(value);
  const { data: allNews = [], isLoading, refetch } = useGetAllNews();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const selected = new Set(newsIds);

  const toggle = (id: string) => {
    const next = selected.has(id)
      ? newsIds.filter((x) => x !== id)
      : [...newsIds, id];
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <NewsItemDrawer
        refetch={() => {
          void refetch();
        }}
        onCreated={(item) => {
          if (item.id && !selected.has(item.id)) {
            onChange([...newsIds, item.id]);
          }
        }}
        trigger={
          <Button type="button" variant="outline" size="sm" className="w-full">
            <Plus size={14} className="mr-1.5" />
            {cmsPhrase(cmsNew, "newsAddButton")}
          </Button>
        }
      />

      <div className="rounded border border-slate-200 bg-slate-50 p-2">
        {isLoading ? (
          <p className="text-xs text-slate-400">
            {cmsNew("cms.new.auto.text.chargement", "Chargement…")}
          </p>
        ) : allNews.length === 0 ? (
          <p className="text-xs text-slate-400">
            {cmsNew(
              "cms.new.auto.text.aucune_actualite",
              "Aucune actualité pour le moment.",
            )}
          </p>
        ) : (
          <ul className="max-h-48 space-y-1 overflow-y-auto">
            {allNews.map((item) => {
              if (!item.id) return null;
              const active = selected.has(item.id);
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => toggle(item.id!)}
                    className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors ${
                      active
                        ? "bg-white font-medium text-slate-900 shadow-sm"
                        : "text-slate-600 hover:bg-white/80"
                    }`}
                  >
                    <span
                      className={`h-3.5 w-3.5 shrink-0 rounded border ${
                        active
                          ? "border-primary bg-primary"
                          : "border-slate-300 bg-white"
                      }`}
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {newsTitle(item, lang)}
                    </span>
                    {item.date ? (
                      <span className="shrink-0 text-[10px] text-slate-400">
                        {new Date(item.date).toLocaleDateString(lang)}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {newsIds.length > 0 && (
          <p className="mt-2 text-[11px] text-slate-500">
            {newsIds.length}{" "}
            {cmsNew("cms.new.pickers.news.selectedCount", "sélectionnée")}
            {newsIds.length > 1 ? "s" : ""}
            <button
              type="button"
              className="ml-2 text-red-600 hover:underline"
              onClick={() => onChange([])}
            >
              {cmsNew("cms.new.pickers.news.clearAll", "Tout effacer")}
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
